/**
 * Drawer para registrar el pago de una cuota de crédito.
 *
 * Flujo simple para MVP:
 *   1. Recibe el credito_id, fetchea próxima cuota pendiente + caja activa
 *   2. Muestra el monto pendiente (capital + interés + mora)
 *   3. Operador elige cuánto pagar (default = todo el pendiente) y forma
 *   4. Llama registrar_pago_credito_admin
 *   5. Muestra desglose del pago: capital/interés/mora aplicados
 *
 * Por ahora solo efectivo. Más adelante agregamos depósito/cheque/transfer
 * (mismo patrón que NuevaOperacionDrawer).
 */
import { useEffect, useState } from 'react'
import { CheckCircle, Printer, Receipt } from '@phosphor-icons/react'
import { DrawerShell } from './DrawerShell'
import { supabase } from '@/lib/supabase'
import { reimprimirComprobantePorMovimientoId } from '@/lib/reimprimirComprobante'

type Props = {
  open: boolean
  onClose: () => void
  creditoId: string
  asociadoNombre: string
  onSuccess: () => void
}

type Cuota = {
  id: string
  numero: number
  fecha_vencimiento: string
  capital: number
  interes: number
  capital_pagado: number
  interes_pagado: number
  mora_acumulada: number
  mora_pagada: number
  estado: string
}

type Caja = {
  id: string
  codigo: string
  nombre: string
}

type PagoResult = {
  pago_id: string
  movimiento_caja_id: string | null
  aplicado_capital: number
  aplicado_interes: number
  aplicado_mora: number
  cuota_pagada: boolean
  credito_cancelado: boolean
  saldo_capital_restante: number
  retiros_cancelados?: number
}

const fmtQ = (n: number) =>
  'Q ' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtFecha = (iso: string) => {
  if (!iso) return '—'
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00')
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' })
}

function traducirError(raw: string): string {
  const s = raw.toLowerCase()
  if (s.includes('ya esta')) return 'Esta cuota ya está pagada.'
  if (s.includes('excede el pendiente')) return raw
  if (s.includes('caja')) return 'No hay una caja activa abierta. Abrí la caja antes de cobrar.'
  if (s.includes('credito esta')) return raw
  return raw
}

export function RegistrarPagoCreditoDrawer({
  open, onClose, creditoId, asociadoNombre, onSuccess,
}: Props) {
  const [cuota, setCuota] = useState<Cuota | null>(null)
  const [caja, setCaja] = useState<Caja | null>(null)
  const [saldoAhorro, setSaldoAhorro] = useState(0)
  const [retiroReservado, setRetiroReservado] = useState(0)
  const [metodo, setMetodo] = useState<'efectivo' | 'ahorro'>('efectivo')
  const [pendiente, setPendiente] = useState({ capital: 0, interes: 0, mora: 0, total: 0 })
  const [montoStr, setMontoStr] = useState('')
  const [notas, setNotas] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState<PagoResult | null>(null)
  const [autoPrinted, setAutoPrinted] = useState(false)

  // Auto-imprimir el recibo apenas se confirma el éxito (con un pelín
  // de delay para que el browser ya tenga la vista renderizada y no
  // dispare el popup blocker antes de la interacción del operador).
  useEffect(() => {
    if (success && !autoPrinted && success.movimiento_caja_id) {
      setAutoPrinted(true)
      const movId = success.movimiento_caja_id
      const t = setTimeout(() => {
        void reimprimirComprobantePorMovimientoId(movId)
      }, 300)
      return () => clearTimeout(t)
    }
  }, [success, autoPrinted])

  // Cargar próxima cuota + caja
  useEffect(() => {
    if (!open) return
    setSuccess(null)
    setError(null)
    setMontoStr('')
    setNotas('')
    setLoading(true)
    setAutoPrinted(false)

    void (async () => {
      try {
        // Próxima cuota pendiente (la de menor número que no está pagada)
        const { data: cuotaData, error: cuotaErr } = await supabase
          .from('credito_cuota')
          .select('*')
          .eq('credito_id', creditoId)
          .not('estado', 'in', '("pagada","castigada")')
          .order('numero', { ascending: true })
          .limit(1)
          .single()
        if (cuotaErr) throw new Error('No hay cuotas pendientes para cobrar')

        const c = cuotaData as Cuota
        setCuota(c)

        const p = {
          capital: Math.max(c.capital - c.capital_pagado, 0),
          interes: Math.max(c.interes - c.interes_pagado, 0),
          mora: Math.max(c.mora_acumulada - c.mora_pagada, 0),
          total: 0,
        }
        p.total = p.capital + p.interes + p.mora
        setPendiente(p)
        setMontoStr(p.total.toFixed(2))

        // Caja activa (NO fatal: el pago con ahorro no la necesita)
        const { data: cajaData } = await supabase
          .from('caja')
          .select('id, codigo, nombre')
          .eq('activa', true)
          .eq('tipo', 'FISICA')
          .limit(1)
          .maybeSingle()
        setCaja((cajaData as Caja) ?? null)

        // Saldo de ahorro del asociado + retiros en proceso (saldo reservado)
        let saldo = 0
        let reservado = 0
        const { data: credRow } = await supabase
          .from('credito').select('asociado_id').eq('id', creditoId).single()
        if (credRow?.asociado_id) {
          const { data: ahoRow } = await supabase
            .from('cuenta_ahorro_corriente')
            .select('saldo').eq('asociado_id', credRow.asociado_id).eq('estado', 'activa')
            .order('abierta_at', { ascending: true }).limit(1).maybeSingle()
          saldo = Number(ahoRow?.saldo ?? 0)

          const { data: retiros } = await supabase
            .from('retiro')
            .select('monto').eq('asociado_id', credRow.asociado_id).eq('estado', 'solicitado')
          reservado = (retiros ?? []).reduce((acc, r) => acc + Number(r.monto ?? 0), 0)
        }
        setSaldoAhorro(saldo)
        setRetiroReservado(reservado)
        // Método por defecto: efectivo si hay caja; si no, ahorro cuando el DISPONIBLE alcanza.
        setMetodo(cajaData ? 'efectivo' : ((saldo - reservado) >= p.total ? 'ahorro' : 'efectivo'))
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Error desconocido')
      } finally {
        setLoading(false)
      }
    })()
  }, [open, creditoId])

  if (!open) return null

  const monto = metodo === 'ahorro' ? pendiente.total : (Number(montoStr) || 0)
  const disponibleAhorro = saldoAhorro - retiroReservado
  const alcanzaAhorro = saldoAhorro >= pendiente.total          // el saldo bruto alcanza
  const alcanzaDisponible = disponibleAhorro >= pendiente.total  // el disponible (sin lo reservado) alcanza
  // El saldo alcanza pero está reservado por un retiro en proceso
  const bloqueadoPorRetiro = metodo === 'ahorro' && alcanzaAhorro && !alcanzaDisponible && retiroReservado > 0
  const valido = metodo === 'ahorro'
    ? !!cuota && alcanzaDisponible
    : !!cuota && !!caja && monto > 0 && monto <= pendiente.total

  const onConfirmar = async (cancelarRetiro = false) => {
    if (!cuota) return
    if (!cancelarRetiro && !valido) return
    setSubmitting(true)
    setError(null)
    try {
      let data: unknown
      if (metodo === 'ahorro') {
        const { data: d, error: rpcErr } = await supabase.rpc('registrar_pago_credito_ahorro_admin', {
          p_credito_cuota_id: cuota.id,
          p_notas: notas.trim() || null,
          p_cancelar_retiro: cancelarRetiro,
        })
        if (rpcErr) throw rpcErr
        data = d
      } else {
        if (!caja) throw new Error('No hay caja activa abierta')
        const { data: d, error: rpcErr } = await supabase.rpc('registrar_pago_credito_admin', {
          p_input: {
            credito_cuota_id: cuota.id,
            caja_id: caja.id,
            monto,
            pagos: [{ tipo: 'efectivo', monto }],
            notas: notas.trim() || null,
          },
        })
        if (rpcErr) throw rpcErr
        data = d
      }
      setSuccess(data as PagoResult)
      onSuccess()
    } catch (e) {
      const raw = e instanceof Error ? e.message : 'Error desconocido'
      setError(traducirError(raw))
    } finally {
      setSubmitting(false)
    }
  }

  // ============ Vista de éxito ============
  if (success) {
    return (
      <DrawerShell onClose={onClose}>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CheckCircle size={28} weight="duotone" color="var(--ok-ink, #0f8a57)" />
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                {success.cuota_pagada ? 'Cuota pagada' : 'Pago parcial registrado'}
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
                {asociadoNombre} · Cuota {cuota?.numero}
              </p>
            </div>
          </div>

          {success.credito_cancelado && (
            <div style={{
              padding: '12px 14px', borderRadius: 10,
              background: 'rgba(15,138,87,0.08)', color: 'var(--ok-ink, #0f8a57)',
              fontSize: 13, fontWeight: 600,
            }}>
              Crédito cancelado · todas las cuotas pagadas.
            </div>
          )}

          <div style={{
            background: 'var(--bg, #fbf7f3)', borderRadius: 12, padding: 16,
            border: '1px solid var(--line)',
          }}>
            <p style={{
              margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
              color: 'var(--muted-foreground)', textTransform: 'uppercase',
            }}>
              Desglose del pago
            </p>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
              {success.aplicado_mora > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Mora</span>
                  <strong style={{ fontFeatureSettings: '"tnum"', color: 'var(--danger, #a32d2d)' }}>{fmtQ(success.aplicado_mora)}</strong>
                </div>
              )}
              {success.aplicado_interes > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Interés</span>
                  <strong style={{ fontFeatureSettings: '"tnum"', color: 'var(--brand-deep)' }}>{fmtQ(success.aplicado_interes)}</strong>
                </div>
              )}
              {success.aplicado_capital > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Capital</span>
                  <strong style={{ fontFeatureSettings: '"tnum"' }}>{fmtQ(success.aplicado_capital)}</strong>
                </div>
              )}
              <div style={{
                borderTop: '1px solid var(--line)', paddingTop: 8, marginTop: 4,
                display: 'flex', justifyContent: 'space-between',
              }}>
                <span style={{ fontWeight: 700 }}>Saldo capital restante</span>
                <strong style={{ fontFeatureSettings: '"tnum"', color: 'var(--brand)' }}>
                  {fmtQ(success.saldo_capital_restante)}
                </strong>
              </div>
            </div>
          </div>

          {success.movimiento_caja_id ? (
            <p style={{ fontSize: 11, color: 'var(--muted-foreground)', textAlign: 'center', marginTop: 8 }}>
              El recibo se está imprimiendo. Si no apareció, click en "Reimprimir".
            </p>
          ) : (
            <p style={{ fontSize: 11, color: 'var(--muted-foreground)', textAlign: 'center', marginTop: 8 }}>
              Pago aplicado con el ahorro del asociado.
              {!!success.retiros_cancelados && success.retiros_cancelados > 0 && (
                <> Se canceló el retiro en proceso para liberar el saldo; avisale al asociado que vuelva a pedirlo si aún lo necesita.</>
              )}
            </p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'auto' }}>
            {!success.credito_cancelado && (
              <button
                className="btn btn-primary"
                onClick={() => {
                  // Reset el estado para cobrar la siguiente cuota sin cerrar
                  setSuccess(null)
                  setAutoPrinted(false)
                  setError(null)
                  setMontoStr('')
                  setNotas('')
                  setLoading(true)
                  // Vuelve a disparar la carga (cuota + caja)
                  void (async () => {
                    const { data: cuotaData, error: cuotaErr } = await supabase
                      .from('credito_cuota')
                      .select('*')
                      .eq('credito_id', creditoId)
                      .not('estado', 'in', '("pagada","castigada")')
                      .order('numero', { ascending: true })
                      .limit(1)
                      .single()
                    if (cuotaErr) {
                      setError('No quedan cuotas pendientes.')
                      setLoading(false)
                      return
                    }
                    const c = cuotaData as Cuota
                    setCuota(c)
                    const p = {
                      capital: Math.max(c.capital - c.capital_pagado, 0),
                      interes: Math.max(c.interes - c.interes_pagado, 0),
                      mora: Math.max(c.mora_acumulada - c.mora_pagada, 0),
                      total: 0,
                    }
                    p.total = p.capital + p.interes + p.mora
                    setPendiente(p)
                    setMontoStr(p.total.toFixed(2))
                    setLoading(false)
                  })()
                }}
              >
                Cobrar siguiente cuota →
              </button>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              {success.movimiento_caja_id && (
                <button
                  className="btn btn-ghost"
                  onClick={async () => {
                    const movId = success.movimiento_caja_id
                    if (!movId) return
                    const r = await reimprimirComprobantePorMovimientoId(movId)
                    if (!r.ok) alert('No pudimos reimprimir: ' + r.error)
                  }}
                  style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Printer size={14} weight="bold" /> Reimprimir
                </button>
              )}
              <button
                className="btn btn-ghost"
                onClick={onClose}
                style={{ flex: 1 }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </DrawerShell>
    )
  }

  // ============ Vista del formulario ============
  return (
    <DrawerShell onClose={onClose}>
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Receipt size={26} weight="duotone" color="var(--brand)" />
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Registrar pago</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
              {asociadoNombre}
            </p>
          </div>
        </div>

        {loading && <p style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>Cargando…</p>}

        {error && !cuota && (
          <div style={{
            padding: '12px 14px', borderRadius: 10,
            background: 'rgba(163,45,45,0.08)', color: 'var(--danger, #a32d2d)',
            fontSize: 13,
          }}>
            {error}
          </div>
        )}

        {cuota && (
          <>
            {/* Próxima cuota */}
            <div style={{
              background: 'var(--surface)', borderRadius: 12, padding: 16,
              border: '1px solid var(--line)',
            }}>
              <p style={{
                margin: 0, fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
                color: 'var(--muted-foreground)', textTransform: 'uppercase',
              }}>
                Cuota {cuota.numero} · Vence {fmtFecha(cuota.fecha_vencimiento)}
              </p>
              <p style={{
                margin: '6px 0 0', fontSize: 26, fontWeight: 800,
                fontFeatureSettings: '"tnum"', color: 'var(--brand)',
              }}>
                {fmtQ(pendiente.total)}
              </p>
              <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 12, color: 'var(--ink-soft)' }}>
                <span>Capital <strong style={{ fontFeatureSettings: '"tnum"' }}>{fmtQ(pendiente.capital)}</strong></span>
                <span>Interés <strong style={{ fontFeatureSettings: '"tnum"' }}>{fmtQ(pendiente.interes)}</strong></span>
                {pendiente.mora > 0 && (
                  <span style={{ color: 'var(--danger)' }}>
                    Mora <strong style={{ fontFeatureSettings: '"tnum"' }}>{fmtQ(pendiente.mora)}</strong>
                  </span>
                )}
              </div>
            </div>

            {/* Monto a pagar */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.5 }}>
                Monto a cobrar
              </label>
              <input
                type="number"
                step="0.01"
                value={metodo === 'ahorro' ? pendiente.total.toFixed(2) : montoStr}
                onChange={(e) => setMontoStr(e.target.value)}
                disabled={metodo === 'ahorro'}
                style={{
                  width: '100%', padding: '12px 14px', borderRadius: 8,
                  border: '1px solid var(--line)', fontSize: 18, fontWeight: 700,
                  fontFamily: 'inherit', fontFeatureSettings: '"tnum"',
                  marginTop: 6, opacity: metodo === 'ahorro' ? 0.6 : 1,
                }}
              />
              <p style={{ fontSize: 11, color: 'var(--muted-foreground)', margin: '4px 0 0' }}>
                {metodo === 'ahorro'
                  ? 'Con ahorro se paga la cuota completa.'
                  : `Se aplica primero a mora, luego interés, después capital. Máx Q ${pendiente.total.toFixed(2)}.`}
              </p>
            </div>

            {/* Forma de pago — efectivo (caja) o ahorro del asociado */}
            <div>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.5, margin: '0 0 6px' }}>
                Forma de pago
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => caja && setMetodo('efectivo')}
                  disabled={!caja}
                  style={{
                    all: 'unset', flex: 1, cursor: caja ? 'pointer' : 'not-allowed',
                    padding: '10px 12px', borderRadius: 8, textAlign: 'center',
                    border: `2px solid ${metodo === 'efectivo' ? 'var(--brand)' : 'var(--line)'}`,
                    background: metodo === 'efectivo' ? 'var(--brand-soft, #ffe4d4)' : 'var(--surface)',
                    opacity: caja ? 1 : 0.5,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700, color: metodo === 'efectivo' ? 'var(--brand-deep)' : 'var(--ink)' }}>Efectivo</div>
                  <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{caja ? `Caja ${caja.codigo}` : 'Sin caja abierta'}</div>
                </button>
                <button
                  type="button"
                  onClick={() => alcanzaAhorro && setMetodo('ahorro')}
                  disabled={!alcanzaAhorro}
                  style={{
                    all: 'unset', flex: 1, cursor: alcanzaAhorro ? 'pointer' : 'not-allowed',
                    padding: '10px 12px', borderRadius: 8, textAlign: 'center',
                    border: `2px solid ${metodo === 'ahorro' ? 'var(--brand)' : 'var(--line)'}`,
                    background: metodo === 'ahorro' ? 'var(--brand-soft, #ffe4d4)' : 'var(--surface)',
                    opacity: alcanzaAhorro ? 1 : 0.5,
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700, color: metodo === 'ahorro' ? 'var(--brand-deep)' : 'var(--ink)' }}>Con ahorro</div>
                  <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                    {retiroReservado > 0 ? `Disp. ${fmtQ(disponibleAhorro)}` : `Saldo ${fmtQ(saldoAhorro)}`}
                  </div>
                </button>
              </div>
              {metodo === 'ahorro' && alcanzaDisponible && (
                <p style={{ fontSize: 11, color: 'var(--muted-foreground)', margin: '6px 0 0' }}>
                  Se debita del ahorro del asociado · su saldo queda en {fmtQ(saldoAhorro - pendiente.total)}. No requiere caja.
                </p>
              )}
              {!alcanzaAhorro && (
                <p style={{ fontSize: 11, color: 'var(--muted-foreground)', margin: '6px 0 0' }}>
                  El ahorro del asociado ({fmtQ(saldoAhorro)}) no alcanza para esta cuota.
                </p>
              )}
              {bloqueadoPorRetiro && (
                <div style={{
                  marginTop: 8, padding: '10px 12px', borderRadius: 8,
                  background: 'rgba(214,138,4,0.09)', border: '1px solid rgba(214,138,4,0.35)',
                  fontSize: 12, color: 'var(--ink)', lineHeight: 1.45,
                }}>
                  <strong>Saldo reservado por un retiro en proceso ({fmtQ(retiroReservado)}).</strong><br />
                  El saldo ({fmtQ(saldoAhorro)}) alcanza, pero está comprometido por un retiro que el
                  asociado pidió. Disponible real: {fmtQ(disponibleAhorro)}. Cancelá el retiro para
                  liberar el saldo y pagar la cuota.
                </div>
              )}
            </div>

            {/* Notas */}
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1.5 }}>
                Notas (opcional)
              </label>
              <input
                type="text"
                value={notas}
                onChange={(e) => setNotas(e.target.value)}
                placeholder="Referencia interna…"
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: '1px solid var(--line)', fontSize: 13, fontFamily: 'inherit',
                  marginTop: 6,
                }}
              />
            </div>

            {error && (
              <div style={{
                padding: '10px 12px', borderRadius: 8,
                background: 'rgba(163,45,45,0.08)', color: 'var(--danger, #a32d2d)',
                fontSize: 13,
              }}>
                {error}
              </div>
            )}

            {bloqueadoPorRetiro ? (
              <button
                className="btn btn-primary"
                onClick={() => onConfirmar(true)}
                disabled={submitting}
                style={{ marginTop: 'auto', background: 'var(--danger, #a32d2d)', borderColor: 'var(--danger, #a32d2d)' }}
              >
                {submitting ? 'Procesando…' : `Cancelar retiro y pagar ${fmtQ(pendiente.total)} con ahorro`}
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={() => onConfirmar(false)}
                disabled={submitting || !valido}
                style={{ marginTop: 'auto' }}
              >
                {submitting
                  ? 'Registrando…'
                  : metodo === 'ahorro' ? `Pagar ${fmtQ(monto)} con ahorro` : `Cobrar ${fmtQ(monto)}`}
              </button>
            )}
          </>
        )}
      </div>
    </DrawerShell>
  )
}
